import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ReportDefinitionOption {
  readonly reportDefinitionId: string;
  readonly reportName: string;
}

interface ScheduleRow {
  readonly reportScheduleId: string;
  readonly reportName: string;
  readonly frequency: string;
  readonly nextRunAt: string;
  readonly deliveryChannel: string;
  readonly status: string;
}

interface NewScheduleForm {
  reportDefinitionId: string;
  scheduleName: string;
  cronExpression: string;
  exportFormat: string;
  deliveryChannel: string;
  recipients: string;
}

const EMPTY_SCHEDULE: NewScheduleForm = {
  reportDefinitionId: '', scheduleName: '', cronExpression: '', exportFormat: 'PDF', deliveryChannel: 'EMAIL', recipients: '',
};

const EXPORT_FORMATS = ['PDF', 'EXCEL', 'CSV', 'WORD', 'JSON', 'XML'] as const;
const DELIVERY_CHANNELS = ['EMAIL', 'MOBILE', 'DOWNLOAD_CENTER', 'SHARED_REPO'] as const;

/** L16.07 — Report scheduler / subscriptions (`/reports/subscriptions`). Lists
 *  scheduled report deliveries and supports create/delete authoring (M4). */
export function M16ReportSubscriptionsPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<ScheduleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<NewScheduleForm>(EMPTY_SCHEDULE);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function load(): Promise<void> {
    setRows(await apiRequest<ScheduleRow[]>('/v1/m16/reports/schedules', { token }));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<ScheduleRow[]>('/v1/m16/reports/schedules', { token })
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  function startEdit(row: ScheduleRow): void {
    setEditingId(row.reportScheduleId);
    setForm({
      reportDefinitionId: '', scheduleName: row.reportName, cronExpression: '',
      exportFormat: 'PDF', deliveryChannel: row.deliveryChannel, recipients: '',
    });
  }

  function cancelEdit(): void {
    setEditingId(null);
    setForm(EMPTY_SCHEDULE);
  }

  async function saveSchedule(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      if (editingId) {
        await apiRequest(`/v1/m16/reports/schedules/${editingId}`, {
          method: 'PUT',
          token,
          body: {
            scheduleName: form.scheduleName,
            cronExpression: form.cronExpression,
            exportFormat: form.exportFormat,
            deliveryChannel: form.deliveryChannel,
            recipients: form.recipients.split(',').map((s) => s.trim()).filter(Boolean),
          },
        });
        setEditingId(null);
      } else {
        await apiRequest('/v1/m16/reports/schedules', {
          method: 'POST',
          token,
          body: {
            reportDefinitionId: form.reportDefinitionId,
            scheduleName: form.scheduleName,
            cronExpression: form.cronExpression,
            exportFormat: form.exportFormat,
            deliveryChannel: form.deliveryChannel,
            recipients: form.recipients.split(',').map((s) => s.trim()).filter(Boolean),
          },
        });
      }
      setForm(EMPTY_SCHEDULE);
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteSchedule(id: string): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(`/v1/m16/reports/schedules/${id}`, { method: 'DELETE', token });
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m16.subscriptions.loading')}</p>;

  // CreateScheduleDto requires reportDefinitionId, scheduleName (min 3) and
  // cronExpression (min 9, the length of "0 0 * * *"). Format and channel come
  // from selects. UpdateScheduleDto makes every field optional, so editing is
  // left as permissive as the API itself.
  const canSaveSchedule = editingId
    ? true
    : form.reportDefinitionId.trim().length > 0 &&
      form.scheduleName.trim().length >= 3 &&
      form.cronExpression.trim().length >= 9;

  return (
    <main aria-label="report-subscriptions">
      <h1>{t('m16.subscriptions.title')}</h1>

      <section aria-label="schedule-builder">
        <h2>{editingId ? t('m16.subscriptions.editSchedule') : t('m16.subscriptions.newSchedule')}</h2>
        <RecordPicker<ReportDefinitionOption>
          endpoint="/v1/m16/reports/definitions"
          idField="reportDefinitionId"
          label={t('m16.subscriptions.placeholderReportId')}
          testId="new-schedule-report"
          disabled={!!editingId}
          value={form.reportDefinitionId}
          optionLabel={(r) => r.reportName}
          onPick={(id) => setForm((f) => ({ ...f, reportDefinitionId: id }))}
        />
        <input aria-label="new-schedule-name" data-testid="new-schedule-name" placeholder={t('m16.subscriptions.placeholderName')}
          value={form.scheduleName} onChange={(e) => setForm((f) => ({ ...f, scheduleName: e.target.value }))} />
        <input aria-label="new-schedule-cron" data-testid="new-schedule-cron" placeholder={t('m16.subscriptions.placeholderCron')}
          value={form.cronExpression} onChange={(e) => setForm((f) => ({ ...f, cronExpression: e.target.value }))} />
        <select aria-label="new-schedule-format" data-testid="new-schedule-format"
          value={form.exportFormat} onChange={(e) => setForm((f) => ({ ...f, exportFormat: e.target.value }))}>
          {EXPORT_FORMATS.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select aria-label="new-schedule-channel" data-testid="new-schedule-channel"
          value={form.deliveryChannel} onChange={(e) => setForm((f) => ({ ...f, deliveryChannel: e.target.value }))}>
          {DELIVERY_CHANNELS.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <input aria-label="new-schedule-recipients" data-testid="new-schedule-recipients" placeholder={t('m16.subscriptions.placeholderRecipients')}
          value={form.recipients} onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))} />
        <button type="button" data-testid="create-schedule-btn" disabled={busy || !canSaveSchedule} onClick={saveSchedule}>
          {editingId ? t('m16.subscriptions.saveBtn') : t('m16.subscriptions.createBtn')}
        </button>
        {editingId && <button type="button" data-testid="cancel-schedule-edit-btn" onClick={cancelEdit}>{t('m16.subscriptions.cancelBtn')}</button>}
      </section>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="report-subscriptions-table">
        <thead>
          <tr><th>{t('m16.subscriptions.colReport')}</th><th>{t('m16.subscriptions.colFrequency')}</th><th>{t('m16.subscriptions.colNextRun')}</th><th>{t('m16.subscriptions.colChannel')}</th><th>{t('m16.subscriptions.colStatus')}</th><th>{t('m16.subscriptions.colEdit')}</th><th>{t('m16.subscriptions.colDelete')}</th></tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.reportScheduleId}>
              <td>{s.reportName}</td>
              <td>{s.frequency}</td>
              <td>{at(s.nextRunAt)}</td>
              <td>{s.deliveryChannel}</td>
              <td>{s.status}</td>
              <td>
                <button type="button" aria-label={`edit-schedule-${s.reportScheduleId}`} disabled={busy}
                  onClick={() => startEdit(s)}>{t('m16.subscriptions.editBtn')}</button>
              </td>
              <td>
                <button type="button" aria-label={`delete-schedule-${s.reportScheduleId}`} disabled={busy}
                  onClick={() => deleteSchedule(s.reportScheduleId)}>{t('m16.subscriptions.deleteBtn')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
